import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createCompany } from "../../api/platform";
import { useApiError } from "../../hooks/useApiError";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../../components/neu/Neu";
import { CompanyFeaturePicker } from "./CompanyFeaturePicker";
import {
  COMPANY_FORM_FIELDS as FIELDS,
  EMPTY_COMPANY,
  REQUIRED_COMPANY_FIELDS,
  toCompanyPayload,
} from "./companyFormFields";

export const CompanyForm = ({ onSaved, onCancel }) => {
  const { t } = useTranslation();
  const describe = useApiError();
  const [form, setForm] = useState(EMPTY_COMPANY);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createCompany(toCompanyPayload(form));
      toast.success(t("platform.created"));
      onSaved();
    } catch (err) {
      toast.error(describe(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NeuCard className="animate-rise-in" data-testid="company-form">
      <form className="grid gap-4 sm:grid-cols-3" onSubmit={submit}>
        {FIELDS.map(([key, label, type]) => (
          <div key={key}>
            <p className="label-xs mb-2">{t(label)}</p>
            <NeuInput
              data-testid={`company-${key}`}
              type={type}
              required={REQUIRED_COMPANY_FIELDS.includes(key)}
              value={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            />
          </div>
        ))}
        <div>
          <p className="label-xs mb-2">{t("platform.status")}</p>
          <NeuSelect
            data-testid="company-status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            {["trial", "active", "suspended", "expired"].map((s) => (
              <option key={s} value={s}>
                {t(`platform.statuses.${s}`)}
              </option>
            ))}
          </NeuSelect>
        </div>
        <CompanyFeaturePicker value={form.features} onChange={(features) => setForm({ ...form, features })} />
        <div className="flex items-end gap-2 sm:col-span-3">
          <NeuButton variant="primary" type="submit" data-testid="company-save" disabled={busy}>
            {t("app.save")}
          </NeuButton>
          <NeuButton type="button" data-testid="company-cancel" onClick={onCancel}>
            {t("app.cancel")}
          </NeuButton>
        </div>
      </form>
    </NeuCard>
  );
};
